/**
 * Chi sei rispetto a questa casa.
 *
 * È la dichiarazione da cui dipende l'unica domanda che conta davvero — *a me
 * cosa resta* — e senza di lei il ledger sa rispondere solo *quanto rende la
 * casa*, che su un mandato è un numero dieci volte più grande e riguarda
 * qualcun altro.
 *
 * Si compila una volta sola. La parte che fa il lavoro nel tempo è "chi paga
 * cosa": serve a non dover rispondere a una domanda per ogni bolletta che
 * entra, e soprattutto a decidere le rotture **da fermi**, prima che ci sia
 * una fattura sul tavolo e due motivi per tirare ognuno dalla propria parte.
 */

import { useEffect, useState } from "react";
import { Check, Handshake } from "lucide-react";
import {
  BASI_FEE,
  ETICHETTE_BASE_FEE,
  ETICHETTE_CATEGORIA,
  TIPI_RAPPORTO,
  type BaseFee,
  type CategoriaCosto,
  type Parte,
  type RapportoInput,
  type TipoRapporto,
} from "@otto/shared";
import { api } from "../lib/api.ts";
import { useStore } from "../lib/store.tsx";
import { Card, Pill, Segmented, Skeleton, cx } from "../ui/primitives.tsx";

/**
 * Le voci su cui gestore e proprietario si dividono davvero il conto.
 *
 * Non sono tutte le categorie: commissioni, canone e fee di gestione non si
 * dividono — o le trattiene il canale, o sono già il passaggio di denaro fra
 * le due parti.
 */
const CATEGORIE_DA_DECIDERE: CategoriaCosto[] = [
  "pulizie",
  "utenze",
  "biancheria",
  "manutenzione",
  "condominio",
  "tassa_soggiorno",
];

const ETICHETTE_TIPO: Record<TipoRapporto, string> = {
  proprieta: "È mio",
  mandato: "In gestione",
  affitto: "In sublocazione",
};

interface Bozza {
  tipo: TipoRapporto;
  controparte: string;
  percentuale: string;
  baseFee: BaseFee;
  feeFissa: string;
  canone: string;
  sogliaManutenzione: string;
  imputazioni: Partial<Record<CategoriaCosto, Parte>>;
}

const VUOTA: Bozza = {
  tipo: "proprieta",
  controparte: "",
  percentuale: "",
  baseFee: "netto_canale",
  feeFissa: "",
  canone: "",
  sogliaManutenzione: "",
  imputazioni: {},
};

/** "1.234,50" → 123450 centesimi. Vuoto → null. */
function inCentesimi(testo: string): number | null {
  const pulito = testo.trim().replace(/\./g, "").replace(",", ".");
  if (pulito === "") return null;
  const numero = Number(pulito);
  return Number.isFinite(numero) && numero >= 0
    ? Math.round(numero * 100)
    : NaN;
}

/** "20" o "17,5" → punti base interi. La virgola mobile qui non entra mai. */
function inPuntiBase(testo: string): number | null {
  const pulito = testo.trim().replace(",", ".");
  if (pulito === "") return null;
  const numero = Number(pulito);
  return Number.isFinite(numero) && numero >= 0 && numero <= 100
    ? Math.round(numero * 100)
    : NaN;
}

function daCentesimi(cents: number | null): string {
  if (cents == null) return "";
  return String(cents / 100).replace(".", ",");
}

function daPuntiBase(bp: number | null): string {
  if (bp == null) return "";
  return String(bp / 100).replace(".", ",");
}

export function RapportoCard({
  listingId,
  onSalvato,
}: {
  listingId: string;
  onSalvato: () => void;
}) {
  const store = useStore();
  const [bozza, setBozza] = useState<Bozza | null>(null);
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    let vivo = true;
    setBozza(null);
    void api
      .rapporto(listingId)
      .then(({ rapporto }) => {
        if (!vivo) return;
        setBozza(
          rapporto
            ? {
                tipo: rapporto.tipo,
                controparte: rapporto.controparte ?? "",
                percentuale: daPuntiBase(rapporto.feePercentualeBp),
                baseFee: rapporto.baseFee,
                feeFissa: daCentesimi(rapporto.feeFissaCents),
                canone: daCentesimi(rapporto.canoneMensileCents),
                sogliaManutenzione: daCentesimi(
                  rapporto.sogliaManutenzioneCents,
                ),
                imputazioni: rapporto.imputazioni,
              }
            : VUOTA,
        );
      })
      .catch(() => {
        if (vivo) setBozza(VUOTA);
      });
    return () => {
      vivo = false;
    };
  }, [listingId]);

  if (!bozza) {
    return (
      <Card className="flex h-full flex-col p-4">
        <Skeleton className="h-24" />
      </Card>
    );
  }

  const modifica = (patch: Partial<Bozza>) =>
    setBozza((b) => (b ? { ...b, ...patch } : b));

  const salva = async () => {
    const numeri = {
      feePercentualeBp: inPuntiBase(bozza.percentuale),
      feeFissaCents: inCentesimi(bozza.feeFissa),
      canoneMensileCents: inCentesimi(bozza.canone),
      sogliaManutenzioneCents: inCentesimi(bozza.sogliaManutenzione),
    };

    if (Object.values(numeri).some((n) => Number.isNaN(n))) {
      store.notify("C'è un numero che non riesco a leggere.");
      return;
    }

    // Le condizioni del rapporto valgono solo per il tipo scelto: tenere in
    // giro un canone su un immobile che è tuo produrrebbe numeri fantasma.
    const input: RapportoInput = {
      tipo: bozza.tipo,
      controparte:
        bozza.tipo === "proprieta" ? null : bozza.controparte.trim() || null,
      feePercentualeBp:
        bozza.tipo === "mandato" ? numeri.feePercentualeBp : null,
      baseFee: bozza.baseFee,
      feeFissaCents: bozza.tipo === "mandato" ? numeri.feeFissaCents : null,
      canoneMensileCents:
        bozza.tipo === "affitto" ? numeri.canoneMensileCents : null,
      sogliaManutenzioneCents:
        bozza.tipo === "proprieta" ? null : numeri.sogliaManutenzioneCents,
      sogliaReclamoCents: null,
      imputazioni: bozza.tipo === "proprieta" ? {} : bozza.imputazioni,
    };

    setSalvando(true);
    try {
      await api.salvaRapporto(listingId, input);
      store.notify(
        bozza.tipo === "proprieta"
          ? "Segnato: questa casa è tua, l'utile resta tutto qui."
          : "Segnato. Da adesso il conto tiene conto di come vi dividete.",
      );
      onSalvato();
    } catch (errore) {
      store.notify(
        errore instanceof Error
          ? errore.message
          : "Non sono riuscito a salvare.",
      );
    } finally {
      setSalvando(false);
    }
  };

  const condiviso = bozza.tipo !== "proprieta";

  return (
    <Card className="flex h-full flex-col p-4">
      <h3 className="mb-1 flex items-center gap-1.5 text-[13px] font-bold text-ink">
        <Handshake size={14} className="text-accent" />
        Il tuo rapporto con questa casa
      </h3>
      <p className="mb-2.5 text-[13px] leading-relaxed text-ink-soft">
        Serve a sapere quanto resta <b>a te</b>, che è un altro numero rispetto
        a quanto rende la casa.
      </p>

      <Segmented
        options={TIPI_RAPPORTO.map((tipo) => ({
          id: tipo,
          label: ETICHETTE_TIPO[tipo],
        }))}
        value={bozza.tipo}
        onChange={(tipo) => modifica({ tipo })}
      />

      {condiviso ? (
        <div className="mt-3 flex flex-col gap-2.5">
          <Campo
            etichetta="Il proprietario"
            valore={bozza.controparte}
            onChange={(controparte) => modifica({ controparte })}
            placeholder="nome e cognome"
          />

          {bozza.tipo === "mandato" ? (
            <fieldset>
              <legend className="mb-1.5 text-[12px] font-semibold text-ink-soft">
                Come vieni pagato
              </legend>
              <div className="flex gap-2">
                <Campo
                  etichetta="percentuale"
                  suffisso="%"
                  valore={bozza.percentuale}
                  onChange={(percentuale) => modifica({ percentuale })}
                />
                <Campo
                  etichetta="più un fisso al mese"
                  prefisso="€"
                  valore={bozza.feeFissa}
                  onChange={(feeFissa) => modifica({ feeFissa })}
                />
              </div>
              <div className="mt-1.5">
                <Segmented
                  options={BASI_FEE.map((base) => ({
                    id: base,
                    label: ETICHETTE_BASE_FEE[base],
                  }))}
                  value={bozza.baseFee}
                  onChange={(baseFee) => modifica({ baseFee })}
                />
              </div>
              <p className="mt-1 text-[12px] leading-relaxed text-ink-faint">
                La stessa percentuale su basi diverse dà numeri diversi: il 30%
                di un mese da 3.000€ vale 900€ sul lordo, 792€ sul netto dalle
                OTA, 609€ sull'utile. Scegli quella dell'accordo.
              </p>
            </fieldset>
          ) : (
            <Campo
              etichetta="Canone che paghi al proprietario, al mese"
              prefisso="€"
              valore={bozza.canone}
              onChange={(canone) => modifica({ canone })}
            />
          )}

          <Campo
            etichetta="Manutenzione: sotto questa cifra la paghi tu"
            prefisso="€"
            valore={bozza.sogliaManutenzione}
            onChange={(sogliaManutenzione) => modifica({ sogliaManutenzione })}
            placeholder="nessuna soglia"
          />
          <p className="-mt-1 text-[12px] leading-relaxed text-ink-faint">
            È la voce su cui si litiga: un wc, una tapparella che cede per
            usura. Deciderla adesso, da fermi, costa un campo; deciderla dopo
            costa una discussione.
          </p>

          <fieldset className="mt-0.5">
            <legend className="mb-1.5 text-[12px] font-semibold text-ink-soft">
              Chi paga cosa
            </legend>
            <div className="flex flex-col gap-1">
              {CATEGORIE_DA_DECIDERE.map((categoria) => (
                <div
                  key={categoria}
                  className="flex items-center justify-between gap-2 text-[13px]"
                >
                  <span className="truncate text-ink-soft">
                    {ETICHETTE_CATEGORIA[categoria]}
                    {categoria === "manutenzione" &&
                    bozza.sogliaManutenzione.trim() !== "" ? (
                      <span className="text-ink-faint"> · sopra la soglia</span>
                    ) : null}
                  </span>
                  <SceltaParte
                    valore={bozza.imputazioni[categoria] ?? "gestore"}
                    onChange={(parte) =>
                      modifica({
                        imputazioni: {
                          ...bozza.imputazioni,
                          [categoria]: parte,
                        },
                      })
                    }
                  />
                </div>
              ))}
            </div>
          </fieldset>
        </div>
      ) : (
        <p className="mt-2.5 text-[13px] leading-relaxed text-ink-faint">
          Niente da dividere: tutto quello che la casa rende resta a te.
        </p>
      )}

      <div className="mt-3 flex justify-end">
        <Pill
          variant="primary"
          onClick={salva}
          loading={salvando}
          icon={<Check size={15} />}
        >
          Salva
        </Pill>
      </div>
    </Card>
  );
}

function Campo({
  etichetta,
  valore,
  onChange,
  prefisso,
  suffisso,
  placeholder,
}: {
  etichetta: string;
  valore: string;
  onChange: (valore: string) => void;
  prefisso?: string;
  suffisso?: string;
  placeholder?: string;
}) {
  return (
    <label className="flex flex-1 flex-col gap-1">
      <span className="text-[12px] font-semibold text-ink-soft">
        {etichetta}
      </span>
      <span className="flex items-center gap-1.5 rounded-field bg-surface-muted px-3">
        {prefisso ? (
          <span className="text-sm text-ink-faint">{prefisso}</span>
        ) : null}
        <input
          type="text"
          inputMode={prefisso || suffisso ? "decimal" : "text"}
          value={valore}
          placeholder={placeholder}
          onChange={(event) => onChange(event.target.value)}
          className="w-full min-w-0 bg-transparent py-2.5 text-[15px] tabular-nums text-ink outline-none placeholder:text-ink-faint"
        />
        {suffisso ? (
          <span className="text-sm text-ink-faint">{suffisso}</span>
        ) : null}
      </span>
    </label>
  );
}

/** Due parole, non un menu a tendina: la scelta è binaria e va vista tutta. */
function SceltaParte({
  valore,
  onChange,
}: {
  valore: Parte;
  onChange: (parte: Parte) => void;
}) {
  const opzioni: Array<{ id: Parte; label: string }> = [
    { id: "gestore", label: "Tu" },
    { id: "proprietario", label: "Proprietario" },
  ];

  return (
    <span className="flex shrink-0 gap-0.5 rounded-pill bg-surface-muted p-0.5">
      {opzioni.map((opzione) => (
        <button
          key={opzione.id}
          type="button"
          aria-pressed={valore === opzione.id}
          onClick={() => onChange(opzione.id)}
          className={cx(
            "rounded-pill px-2.5 py-1 text-[12px] font-bold transition-all duration-quick ease-soft",
            valore === opzione.id
              ? "bg-accent text-ink-inverse"
              : "text-ink-faint hover:text-ink",
          )}
        >
          {opzione.label}
        </button>
      ))}
    </span>
  );
}
